import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, TrendingUp, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const stats = [
    { value: '186+', label: 'Listings Analyzed', icon: TrendingUp },
    { value: '10+', label: 'Smart Algorithms', icon: Sparkles },
    { value: '98%', label: 'Price Accuracy', icon: CheckCircle },
  ];

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background glow effects */}
      <div className="hero-glow top-20 -right-40 opacity-60 hidden md:block" />
      <div className="hero-glow bottom-0 -left-40 opacity-40 hidden md:block" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4 sm:mb-6"
            >
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
              <span className="text-xs sm:text-sm font-medium text-primary">Powered by Real-Time eBay Data</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-4 sm:mb-6"
            >
              <span className="text-slate-200">Turn Trades</span>
              <br />
              <span className="text-gradient">into Treasure</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-base sm:text-lg md:text-xl text-slate-400 max-w-xl mb-6 sm:mb-8 leading-relaxed"
            >
              Get instant, accurate resale estimates powered by real-time eBay data. 
              The smart trade-in pricing tool built for electronics businesses.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12"
            >
              <a
                href="https://apps.apple.com/app/id6759179016"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
                data-testid="hero-cta-primary"
              >
                <Button className="btn-primary flex items-center justify-center gap-2 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto">
                  Download for iOS
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </a>
              <button
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary text-base sm:text-lg py-5 sm:py-6 w-full sm:w-auto"
                data-testid="hero-cta-secondary"
              >
                Explore Features
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-3 gap-2 sm:gap-4"
            >
              {stats.map((stat, index) => (
                <div key={index} className="stat-card text-center p-2 sm:p-4" data-testid={`stat-${index}`}>
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary mx-auto mb-1 sm:mb-2" />
                  <div className="text-lg sm:text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative flex justify-center lg:justify-end order-1 lg:order-2 mb-6 lg:mb-0"
          >
            <div className="relative">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-[3rem] blur-3xl scale-110" />
              
              {/* Phone frame */}
              <div className="phone-mockup relative bg-[#1A1A2E] rounded-[2rem] sm:rounded-[2.5rem] p-2 sm:p-3 border border-white/10 glow-purple">
                <div className="relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_device-valuation/artifacts/zffamtp2_IMG_3585_1242x2688.jpeg"
                    alt="2A Valuence App Screenshot"
                    className="w-[200px] sm:w-[240px] md:w-[280px] lg:w-[320px] h-auto"
                    data-testid="hero-phone-mockup"
                  />
                </div>
                
                {/* Floating price card - hidden on small mobile */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, x: 50 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="absolute -right-2 sm:-right-4 top-1/4 bg-[#1A1A2E]/95 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-4 border border-white/10 shadow-2xl hidden xs:block"
                  data-testid="floating-price-card"
                >
                  <div className="text-[10px] sm:text-xs text-slate-400 mb-1">Recommended Offer</div>
                  <div className="text-lg sm:text-2xl font-bold text-[#10B981]">$847</div>
                  <div className="text-[10px] sm:text-xs text-slate-500">iPhone 15 Pro Max</div>
                </motion.div>

                {/* Floating stats card - hidden on small mobile */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, x: -50 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ delay: 1, duration: 0.4 }}
                  className="absolute -left-2 sm:-left-4 bottom-1/3 bg-[#1A1A2E]/95 backdrop-blur-lg rounded-lg sm:rounded-xl p-2 sm:p-3 border border-white/10 shadow-2xl hidden xs:block"
                  data-testid="floating-stats-card"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-[10px] sm:text-xs text-slate-400">Profit Margin</div>
                      <div className="text-xs sm:text-sm font-bold text-white">+23%</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
